import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { useAuthContext } from "@asgardeo/auth-react";
import abcIcon from "../../../assets/images/ABC.png";

function MenuCard({ showMobileSignin = false, dropdownClassName = "absolute right-0" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { signIn } = useAuthContext();

  const handleStatsClick = () => {
    window.location.href = "/stats";
  };

  const handlePerformanceClick = () => {
    window.location.href = "/benchmark";
  };

  const handleSignInClick = () => {
    const urlString = window.location.search;
    if (urlString) {
      sessionStorage.setItem("urlString", urlString);
    }
    const currentPath = window.location.pathname;
    sessionStorage.setItem("redirectAfterLogin", currentPath);
    setIsOpen(false);
    signIn();
  };

  const toggleMenu = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    document.documentElement.classList.toggle("dark", newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
  };

  useEffect(() => {
    const storedDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(storedDarkMode);
    document.documentElement.classList.toggle("dark", storedDarkMode);

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={menuRef} className="relative z-[110] inline-block text-pl-text-tertiary whitespace-nowrap">
      <button className="h-8 w-8 flex justify-center items-center hover:bg-pl-bg-subtle rounded-pl transition-colors duration-150" onClick={toggleMenu}>
        <Menu size={20} strokeWidth={1.5} />
      </button>

      {isOpen && (
        <div
          className={`${dropdownClassName} z-[110] text-14 bg-pl-bg-elevated rounded-10 shadow-md border border-pl-border overflow-hidden text-pl-text-primary`}
        >
          {showMobileSignin && (
            <>
              <button
                className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
                onClick={handleSignInClick}
              >
                <div className="flex items-center">
                  <svg
                    className="fill-current"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="black"
                    xmlns="http://www.w3.org/2000/svg"
                    fillOpacity="0.6"
                  >
                    <path d="M10 17L11.41 15.59L8.83 13H18V11H8.83L11.41 8.41L10 7L5 12L10 17ZM19 19H12V21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3H12V5H19V19Z" />
                  </svg>
                </div>
                <div className="ml-2">Sign in</div>
              </button>
              <hr className="border-t border-pl-border" />
            </>
          )}

          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/help")}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M9 16H11V14H9V16ZM10 0C8.68678 0 7.38642 0.258658 6.17317 0.761205C4.95991 1.26375 3.85752 2.00035 2.92893 2.92893C1.05357 4.8043 0 7.34784 0 10C0 12.6522 1.05357 15.1957 2.92893 17.0711C3.85752 17.9997 4.95991 18.7362 6.17317 19.2388C7.38642 19.7413 8.68678 20 10 20C12.6522 20 15.1957 18.9464 17.0711 17.0711C18.9464 15.1957 20 12.6522 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17317C18.7362 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM10 4C8.93913 4 7.92172 4.42143 7.17157 5.17157C6.42143 5.92172 6 6.93913 6 8H8C8 7.46957 8.21071 6.96086 8.58579 6.58579C8.96086 6.21071 9.46957 6 10 6C10.5304 6 11.0391 6.21071 11.4142 6.58579C11.7893 6.96086 12 7.46957 12 8C12 10 9 9.75 9 13H11C11 10.75 14 10.5 14 8C14 6.93913 13.5786 5.92172 12.8284 5.17157C12.0783 4.42143 11.0609 4 10 4Z" />
              </svg>
            </div>
            <div className="ml-2">Help</div>
          </button>
          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/search")}
          >
            <div className="flex items-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="black"
                xmlns="http://www.w3.org/2000/svg"
                className="fill-current"
                fillOpacity="0.6"
              >
                <path d="M12 3L2 8l10 5 10-5-10-5zm0 7.18L5.62 8 12 4.82 18.38 8 12 10.18zM2 14l10 5 10-5-2.22-1.11L12 16.68 4.22 12.89 2 14z" />
              </svg>
            </div>
            <div className="ml-2">Case law</div>
          </button>
          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/reports")}
          >
            <div className="flex items-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="black"
                xmlns="http://www.w3.org/2000/svg"
                className="fill-current"
                fillOpacity="0.6"
              >
                <path d="M6 2C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2H6ZM13 3.5L18.5 9H14C13.45 9 13 8.55 13 8V3.5ZM6 4H12V8C12 9.1 12.9 10 14 10H19V20H6V4Z" />
              </svg>
            </div>
            <div className="ml-2">Law reports</div>
          </button>
          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/legislations")}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M6 2C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2H6ZM13 3.5L18.5 9H14C13.45 9 13 8.55 13 8V3.5ZM6 4H12V8C12 9.1 12.9 10 14 10H19V20H6V4ZM8 13H16V15H8V13ZM8 17H14V19H8V17Z" />
              </svg>
            </div>
            <div className="ml-2">Legislations</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/translate")}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z"/>
              </svg>
            </div>
            <div className="ml-2">Translate</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/dictionary")}
          >
            <div className="flex items-center">
              <img
                src={abcIcon}
                alt="ABC"
                className="w-6 h-6 filter saturate-[1200%] hue-rotate-[305deg] brightness-[95%] contrast-[95%]"
              />
            </div>
            <div className="ml-2">Dictionary</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={handlePerformanceClick}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M11 9.47V11H14.76L13 14.53V13H9.24L11 9.47ZM13 1L6 15H11V23L18 9H13V1Z" />
              </svg>
            </div>
            <div className="ml-2">Performance</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={handleStatsClick}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M21 8C19.5 8 18.7 9.4 19.1 10.5L15.5 14.1C15.2 14 14.8 14 14.5 14.1L11.9 11.5C12.3 10.4 11.5 9 10 9C8.6 9 7.7 10.4 8.1 11.5L3.5 16C2.4 15.7 1 16.5 1 18C1 19.1 1.9 20 3 20C4.4 20 5.3 18.6 4.9 17.5L9.4 12.9C9.7 13 10.1 13 10.4 12.9L13 15.5C12.7 16.5 13.5 18 15 18C16.5 18 17.3 16.6 16.9 15.5L20.5 11.9C21.6 12.2 23 11.4 23 10C23 8.9 22.1 8 21 8Z" />
              </svg>
            </div>
            <div className="ml-2">Stats</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={() => navigate("/about")}
          >
            <div className="flex items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                <path d="M11 9H13V7H11M12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" />
              </svg>
            </div>
            <div className="ml-2">About</div>
          </button>

          <hr className="border-t border-pl-border" />
          <button
            className="flex items-center w-full px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
            onClick={toggleDarkMode}
          >
            <div className="flex items-center w-full">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-current" fillOpacity="0.6">
                 {darkMode ? (
                    <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
                  ) : (
                    <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z" />
                  )}
              </svg>
              <div className="ml-2">{darkMode ? "Light mode" : "Dark mode"}</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

export default MenuCard;
